import React, { forwardRef, useEffect, useImperativeHandle, useState, useMemo, } from "react";
import PropTypes from 'prop-types';

import FormFields from "./formField";
import { getNewFormValidationRules } from "../../utils/Validation/GetValidationRule";
import { ValidateAll, Validate, ValidateFileField, ValidateImageField } from "../../utils/Validation/Validation";

const FormCreator = forwardRef(({
  initialState,
  config,
  onFormDataChange,
  onColumnChange,
  onFieldBlure,
  onActionChange,
  ...otherProps
}, ref) => {

  const { formFields, section, formSetting, initialState: configInitialState } = config;

  const [formData, setFormData] = useState(initialState);
  const [validState, setValidState] = useState({
    isValid: true,
    error: {},
  });

  const validationRules = useMemo(() => getNewFormValidationRules(section), [section]);

  useEffect(() => {
    setFormData(configInitialState);
    setValidState({
      isValid: true,
      error: {},
    });
  }, [configInitialState]);

  useImperativeHandle(ref, () => ({
    getFormData: () => {
      const validation = handleValidation();
      return validation.isValid ? formData : null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [formData, validState, validationRules]);


  const handleValidation = () => {
    const validation = ValidateAll(formData, validationRules);
    setValidState(validation);
    return validation;
  };

  const handleStateChange = (updatedData) => {
    setFormData(updatedData);
    onFormDataChange?.(updatedData);
  };

  const handleOnValidateField = (dataField, updatedData = []) => {
    const validation = validationRules[dataField] ? Validate(updatedData || formData, validationRules, dataField) : null;
    if (!validation) return;

    const validationObj = { ...validState };
    if (!validation.isValid) {
      validationObj.error[dataField] = validation.error[dataField];
      validationObj.isValid = false;
    } else {
      delete validationObj.error[dataField];
      validationObj.isValid = Object.keys(validationObj.error).length === 0;
    }
    setValidState(validationObj);
  };

  const handleFileValidation = (files, dataField, fileValidationState, updatedData) => {
    if (!files || !Array.isArray(files)) return;

    const validationObj = { ...validState, error: { ...validState.error } };

    files.forEach((file, index) => {
      const validation = validationRules[dataField]
        ? ValidateFileField(
          file.file,
          fileValidationState?.fieldValiadtionRules,
          updatedData || formData,
          fileValidationState?.acceptedFileFormats,
          fileValidationState.minFileSizeInMB,
          fileValidationState.maxFileSizeInMB,
          dataField
        )
        : null;

      if (validation && !validation.isValid) {
        validationObj.error[`${dataField}`] = validation.messages[0].message;
      } else {
        delete validationObj.error[`${dataField}`];
      }
    });

    // Update the overall `isValid` state based on error presence
    validationObj.isValid = Object.keys(validationObj.error).length === 0;
    if(validationObj.isValid){
      onFieldBlure?.(dataField, updatedData);
    }
    setValidState(validationObj);
  };

  const handleImageValidation = (files, dataField, fileValidationState, updatedData) => {
    if (!files || !Array.isArray(files)) return;

    const validationObj = { ...validState, error: { ...validState.error } };

    files.forEach((file, index) => {
      const validation = validationRules[dataField]
        ? ValidateImageField(file.file, fileValidationState?.fieldValiadtionRules, updatedData || formData, fileValidationState, file.image)
        : null;

      if (validation && !validation.isValid) {
        validationObj.error[`${dataField}`] = validation.messages[0].message;
      } else {
        delete validationObj.error[`${dataField}`];
      }
    });

    // Update the overall `isValid` state based on error presence
    validationObj.isValid = Object.keys(validationObj.error).length === 0;

    setValidState(validationObj);
  };

  return section ? (
    <FormFields
      sections={section}
      fields={formFields}
      formData={formData || {}}
      validState={validState}
      onFormStateChange={handleStateChange}
      onFileValidation={handleFileValidation}
      onImageValidation={handleImageValidation}
      onUpdateValidation={handleOnValidateField}
      onFieldBlure={onFieldBlure}
      onFormFieldChange={onColumnChange}
      onActionChange={onActionChange}
      formSetting={formSetting}
      fieldValiadtionRules={validationRules}
      {...otherProps}  // Pass down other props to FormFields
    />
  ) : (
    <div>No fields configured</div>
  );

});

FormCreator.propTypes = {
  initialState: PropTypes.object,
  config: PropTypes.shape({
    formFields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        label: PropTypes.string,
        value: PropTypes.any,
      })
    ),
    section: PropTypes.object,
    formSetting: PropTypes.shape({
      isViewOnly: PropTypes.bool,
    }),
    initialState: PropTypes.object,
  }).isRequired,
  onFormDataChange: PropTypes.func,
  onColumnChange: PropTypes.func,
  onFieldBlure: PropTypes.func,
  onActionChange: PropTypes.func,
  otherProps: PropTypes.object,
};


export default React.memo(FormCreator);